/**
 * Modal form for adding custom charts to reports
 *
 * Features:
 * - Chart type selector (bar, pie, line, stacked-bar, heatmap, multi-line)
 * - Dynamic dimension selectors based on chart type
 * - Optional date aggregation (month / year)
 * - Optional sort order
 * - Optional filter (same pattern as AddStatCardModal)
 * - Supports both ADD and EDIT modes
 */

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  FormHelperText,
  InputLabel,
  Stack,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Collapse,
  Chip,
} from "@mui/material";
import { ExpandMore, ExpandLess, Settings } from "@mui/icons-material";
import { useState, useEffect, useMemo, useCallback } from "react";
import type { ChartConfig, ConfigDrivenReportConfig, ChartType } from "@hooks/use-reports/types";
import {
  filterColumnsForChartDimension,
  getDimensionRequirementsForChart,
  validateChartDimensions,
} from "./utils";
import { ReportFilterSection } from "../report-config/filter-section";
import { useReportFieldOptions } from "../report-config/use-report-field-options";import { isDateField } from "@hooks/use-reports/utils";import {
  CHART_TYPE_OPTIONS,
  SORT_OPTIONS,
  DATE_AGGREGATION_OPTIONS,
  WIDTH_OPTIONS,
  EMPTY_CHART_FORM_STATE,
  type ChartFormState,
} from "../reporting-constants";
import { useReportFormModal } from "@hooks/use-report-form-modal";

export interface AddChartModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (chartConfig: ChartConfig) => void;
  reportConfig: ConfigDrivenReportConfig;
  editingChartId?: string | null;
  editingConfig?: ChartConfig | null;
  onUpdate?: (chartId: string, chartConfig: ChartConfig) => void;
}

export const AddChartModal = ({
  open,
  onClose,
  onConfirm,
  reportConfig,
  editingChartId = null,
  editingConfig = null,
  onUpdate = () => {},
}: AddChartModalProps) => {
  // Form state management using generic hook
  const { formState, setFormState, formErrors, updateField, updateDimension, validateForm: validateFormBase, resetForm, populateFromConfig } = useReportFormModal<ChartFormState>({
    emptyState: EMPTY_CHART_FORM_STATE,
    onValidate: (state) => {
      const errors: Record<string, string> = {};
      if (!state.title.trim()) {
        errors.title = "Title is required";
      }
      const dimValidation = validateChartDimensions(state.dimensions, state.type);
      if (!dimValidation.isValid) {
        errors.dimensions = dimValidation.errorMessage || "Invalid dimension selection";
      }
      return { isValid: Object.keys(errors).length === 0, errors };
    },
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showBasicConfig, setShowBasicConfig] = useState(true);
  const [showOptional, setShowOptional] = useState(false);
  const [showDateAggregation, setShowDateAggregation] = useState(false);
  const [showLayoutOptions, setShowLayoutOptions] = useState(false);
  const {
    availableColumns,
    filterableColumns,
    filterColumnOptions,
    dynamicFilterLoading,
  } = useReportFieldOptions({
    reportConfig,
    filterColumn: formState.filterColumn,
    useFilter: formState.useFilter,
  });

  const dimensionRequirements = useMemo(
    () => getDimensionRequirementsForChart(formState.type),
    [formState.type]
  );

  // True when at least one selected dimension maps to a date/datetime field
  const hasDateDimension = useMemo(
    () =>
      formState.dimensions.some((dimId) => {
        if (!dimId) return false;
        // Use the isDateField utility which checks field name patterns (date, time, year, month, etc.)
        return isDateField(dimId);
      }),
    [formState.dimensions]
  );

  const dimensionOptionsPerIndex = useMemo(() => {
    return formState.dimensions.map((_, idx) =>
      filterColumnsForChartDimension(availableColumns, formState.type, idx)
    );
  }, [availableColumns, formState.type, formState.dimensions]);

  // Clear dateAggregation when the selected dimensions no longer include a date field
  useEffect(() => {
    if (!hasDateDimension && formState.dateAggregation) {
      updateField("dateAggregation", "");
    }
  }, [hasDateDimension, formState.dateAggregation, updateField]);

  // Reset / populate form when modal opens
  useEffect(() => {
    if (open) {
      if (editingConfig) {
        // Decode dateAggregation: ChartConfig stores it as ["month"] or ["year", ...]
        let dateAgg: "" | "month" | "year" = "";
        if (editingConfig.dateAggregation && editingConfig.dateAggregation.length > 0) {
          const granularity = editingConfig.dateAggregation[0];
          if (granularity === "month" || granularity === "year") dateAgg = granularity;
        }

        populateFromConfig({
          title: editingConfig.title || "",
          type: editingConfig.type,
          width: editingConfig.width || "half",
          dimensions: editingConfig.dimensions ? [...editingConfig.dimensions] : [null],
          dateAggregation: dateAgg,
          sort: editingConfig.sort?.order || "",
          tooltip: editingConfig.tooltip || "",
          filterColumn: editingConfig.filter?.column || "",
          filterValues: editingConfig.filter?.values ? [...editingConfig.filter.values] : [],
          useFilter: Boolean(editingConfig.filter),
        });
      } else {
        resetForm();
      }
      setShowOptional(false);
      setShowDateAggregation(false);
      setShowLayoutOptions(false);
    }
  }, [open, editingConfig, populateFromConfig, resetForm]);

  // Changing chart type resets dimensions to the correct count
  const handleTypeChange = useCallback(
    (newType: ChartType) => {
      const req = getDimensionRequirementsForChart(newType);
      setFormState((prev) => ({
        ...prev,
        type: newType,
        dimensions: new Array(req.count).fill(null),
      }));
    },
    [setFormState]
  );

  const handleSubmit = useCallback(async () => {
    if (!validateFormBase()) return;
    setIsSubmitting(true);

    try {
      const selectedDimensions = formState.dimensions.filter(
        (d): d is string => d !== null && d !== undefined && d !== ""
      );

      // Build dateAggregation tuple if applicable
      const dateAggregation: ChartConfig["dateAggregation"] =
        formState.dateAggregation
          ? [formState.dateAggregation]
          : undefined;

      const configBase: ChartConfig = {
        id: "", // Assigned by useReportLayout
        title: formState.title.trim(),
        type: formState.type,
        dimensions: selectedDimensions,
        width: formState.width,
        ...(formState.sort ? { sort: { order: formState.sort as any } } : {}),
        ...(formState.tooltip ? { tooltip: formState.tooltip } : {}),
        ...(dateAggregation ? { dateAggregation, isDateChart: true } : {}),
      };

      const chartConfig: ChartConfig =
        formState.filterColumn && formState.filterValues.length > 0
          ? { ...configBase, filter: { column: formState.filterColumn, values: formState.filterValues } }
          : configBase;

      if (editingChartId) {
        onUpdate(editingChartId, chartConfig);
      } else {
        onConfirm(chartConfig);
      }

      resetForm();
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  }, [formState, validateFormBase, onConfirm, onUpdate, editingChartId, onClose, resetForm]);

  const isFormValid =
    formState.title.trim().length > 0 &&
    validateChartDimensions(formState.dimensions, formState.type).isValid;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editingChartId ? "Edit Custom Chart" : "Add Custom Chart"}</DialogTitle>

      <DialogContent sx={{ minHeight: "500px", overflow: "auto", pt: 2 }}>
        <Stack spacing={2}>
          {/* Validation alert */}
          {Object.keys(formErrors).length > 0 && (
            <Alert severity="error">{Object.values(formErrors)[0]}</Alert>
          )}

          {/* ── Basic Configuration ── */}
          <Box>
            <Button
              onClick={() => setShowBasicConfig((v) => !v)}
              size="small"
              sx={{ p: 0, mb: 1, display: "flex", alignItems: "center", gap: 0.5 }}
            >
              {showBasicConfig ? <ExpandLess /> : <ExpandMore />}
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Basic Configuration
              </Typography>
            </Button>

            <Collapse in={showBasicConfig} timeout="auto" unmountOnExit>
              <Stack spacing={2}>
                <TextField
                  label="Title"
                  placeholder="e.g., 'Assets by Site', 'Status Breakdown'"
                  value={formState.title}
                  onChange={(e) => updateField("title", e.target.value)}
                  fullWidth
                  size="small"
                  error={Boolean(formErrors.title)}
                  helperText={formErrors.title || "Display name for this chart"}
                />

                <Box sx={{ display: "flex", gap: 2 }}>
                  {/* Chart Type */}
                  <FormControl fullWidth size="small" sx={{ flex: 2 }}>
                    <InputLabel id="chart-type-label">Chart Type</InputLabel>
                    <Select
                      labelId="chart-type-label"
                      value={formState.type}
                      label="Chart Type"
                      onChange={(e) => handleTypeChange(e.target.value as ChartType)}
                    >
                      {CHART_TYPE_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      {CHART_TYPE_OPTIONS.find((o) => o.value === formState.type)?.description}
                    </FormHelperText>
                  </FormControl>

                  {/* Width */}
                  <FormControl fullWidth size="small" sx={{ flex: 1 }}>
                    <InputLabel id="chart-width-label">Width</InputLabel>
                    <Select
                      labelId="chart-width-label"
                      value={formState.width}
                      label="Width"
                      onChange={(e) => updateField("width", e.target.value)}
                    >
                      {WIDTH_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                {/* Dimensions */}
                <Box>
                  {formState.dimensions.map((dimValue, idx) => (
                    <FormControl
                      key={idx}
                      fullWidth
                      size="small"
                      sx={{ mb: idx < formState.dimensions.length - 1 ? 1 : 0 }}
                    >
                      <InputLabel id={`dim-${idx}-label`}>
                        {dimensionRequirements.labels[idx] || `Field ${idx + 1}`}
                      </InputLabel>
                      <Select
                        labelId={`dim-${idx}-label`}
                        value={dimValue || ""}
                        label={dimensionRequirements.labels[idx] || `Field ${idx + 1}`}
                        onChange={(e) => updateDimension(idx, e.target.value || null)}
                        error={Boolean(formErrors.dimensions)}
                      >
                        {dimensionOptionsPerIndex[idx]?.map((col) => (
                          <MenuItem key={col.id} value={col.id}>
                            {col.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {idx === formState.dimensions.length - 1 && (
                        <FormHelperText>{dimensionRequirements.description}</FormHelperText>
                      )}
                    </FormControl>
                  ))}

                  {formErrors.dimensions && (
                    <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
                      {formErrors.dimensions}
                    </Typography>
                  )}
                </Box>
              </Stack>
            </Collapse>
          </Box>

          {/* ── Optional (but powerful) Configurations ── */}
          <Box>
            <Button
              onClick={() => setShowOptional((v) => !v)}
              size="small"
              sx={{ p: 0, mb: 1, display: "flex", alignItems: "center", gap: 0.5 }}
            >
              {showOptional ? <ExpandLess /> : <ExpandMore />}
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Optional Configurations
              </Typography>
            </Button>

            {/* Display chips for applied optional configs OUTSIDE the collapse */}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: showOptional ? 2 : 0 }}>
              {formState.filterColumn && formState.filterValues.length > 0 && (
                <Chip
                  size="small"
                  label={`Filter: ${formState.filterValues.join(", ")}`}
                  onDelete={() => {
                    updateField("filterColumn", "");
                    updateField("filterValues", []);
                  }}
                  icon={<Settings />}
                  onClick={() => updateField("useFilter", true)}
                  variant="outlined"
                />
              )}
              {formState.dateAggregation && (
                <Chip
                  size="small"
                  label={`Date Agg: ${formState.dateAggregation}`}
                  onDelete={() => updateField("dateAggregation", "")}
                  icon={<Settings />}
                  onClick={() => {
                    setShowOptional(true);
                    setShowDateAggregation(true);
                  }}
                  variant="outlined"
                />
              )}
              {formState.tooltip && (
                <Chip
                  size="small"
                  label={`Tooltip: ${formState.tooltip.substring(0, 20)}${formState.tooltip.length > 20 ? "..." : ""}`}
                  onDelete={() => updateField("tooltip", "")}
                  icon={<Settings />}
                  onClick={() => {
                    setShowOptional(true);
                    setShowLayoutOptions(true);
                  }}
                  variant="outlined"
                />
              )}
              {formState.sort && (
                <Chip
                  size="small"
                  label={`Sort: ${formState.sort}`}
                  onDelete={() => updateField("sort", "")}
                  icon={<Settings />}
                  onClick={() => {
                    setShowOptional(true);
                    setShowLayoutOptions(true);
                  }}
                  variant="outlined"
                />
              )}
            </Box>

            <Collapse in={showOptional} timeout="auto" unmountOnExit>
              <Stack spacing={1.5}>

                {/* Filter Section */}
                <ReportFilterSection
                  expanded={formState.useFilter}
                  title="Add Filter"
                  titleVariant="caption"
                  titleColor="text.secondary"
                  filterColumn={formState.filterColumn}
                  filterValues={formState.filterValues}
                  availableColumns={availableColumns}
                  filterableColumns={filterableColumns}
                  filterColumnOptions={filterColumnOptions}
                  dynamicFilterLoading={dynamicFilterLoading}
                  onToggle={() => updateField("useFilter", !formState.useFilter)}
                  onFilterColumnChange={(value) => {
                    updateField("filterColumn", value);
                    updateField("filterValues", []);
                  }}
                  onFilterValuesChange={(values) => updateField("filterValues", values)}
                />

                {/* Date Aggregation Section */}
                {hasDateDimension && (
                  <Box>
                    <Button
                      onClick={() => setShowDateAggregation(!showDateAggregation)}
                      size="small"
                      sx={{ p: 0, display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      {showDateAggregation ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                      <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>
                        Date Aggregation
                      </Typography>
                    </Button>

                    <Collapse in={showDateAggregation} timeout="auto" unmountOnExit>
                      <Stack spacing={1.5} sx={{ mt: 1 }}>
                        <FormControl fullWidth size="small">
                          <InputLabel id="date-agg-label">Date Aggregation</InputLabel>
                          <Select
                            labelId="date-agg-label"
                            value={formState.dateAggregation}
                            label="Date Aggregation"
                            onChange={(e) => updateField("dateAggregation", e.target.value)}
                          >
                            {DATE_AGGREGATION_OPTIONS.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Stack>
                    </Collapse>
                  </Box>
                )}

                {/* Layout Options Section */}
                <Box>
                  <Button
                    onClick={() => setShowLayoutOptions(!showLayoutOptions)}
                    size="small"
                    sx={{ p: 0, display: "flex", alignItems: "center", gap: 0.5 }}
                  >
                    {showLayoutOptions ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                    <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>
                      Layout Options
                    </Typography>
                  </Button>

                  <Collapse in={showLayoutOptions} timeout="auto" unmountOnExit>
                    <Stack spacing={1.5} sx={{ mt: 1 }}>
                      <TextField
                        label="Tooltip"
                        placeholder="Hover text to explain this chart"
                        value={formState.tooltip}
                        onChange={(e) => updateField("tooltip", e.target.value)}
                        fullWidth
                        size="small"
                        multiline
                        rows={2}
                      />

                      <FormControl fullWidth size="small">
                        <InputLabel id="sort-label">Sort Order</InputLabel>
                        <Select
                          labelId="sort-label"
                          value={formState.sort}
                          label="Sort Order"
                          onChange={(e) => updateField("sort", e.target.value)}
                        >
                          {SORT_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Stack>
                  </Collapse>
                </Box>

              </Stack>
            </Collapse>
          </Box>

        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={!isFormValid || isSubmitting}
          sx={{ position: "relative" }}
        >
          {isSubmitting && (
            <CircularProgress size={24} sx={{ position: "absolute", left: "50%", marginLeft: "-12px" }} />
          )}
          <span style={{ visibility: isSubmitting ? "hidden" : "visible" }}>
            {editingChartId ? "Update Chart" : "Add Chart"}
          </span>
        </Button>
      </DialogActions>
    </Dialog>
  );
};
